import React from "react";
import DataGrid, { Column, Editing, Export, FilterPanel, GroupPanel, Pager, Paging, Scrolling, SearchPanel, Sorting } from "devextreme-react/data-grid";
import { LoadIndicator, LoadPanel } from "devextreme-react";
import { AxiosResponse } from "axios";
import lookupService from "../../services/LookupService";
import { onEditCanceledEvent, onExportingEvent } from "../../types/DevExtremeTypes";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";

interface TextStyle {
    id: string | number;
    value: string;
    displayName: string | null;
    subTypeId: string | null;
    typeId: string | null;
    parentMappingId: string | null;
}
interface PromotionTextStylesGridProps { refreshSignal?: boolean; }
interface PromotionTextStylesGridState { data: TextStyle[]; showProgressIndicator: boolean; loadPanelVisible: boolean; errorMessage: any[]; }

export default class PromotionTextStylesGrid extends React.Component<PromotionTextStylesGridProps, PromotionTextStylesGridState> {
    private readonly lookupService = new lookupService();
    state: PromotionTextStylesGridState = { data: [], showProgressIndicator: false, loadPanelVisible: false, errorMessage: [] };

    componentDidMount() { this.loadData(); }
    componentDidUpdate(previousProps: PromotionTextStylesGridProps) {
        if (previousProps.refreshSignal !== this.props.refreshSignal) this.loadData();
    }
    loadData = () => {
        this.setState({ showProgressIndicator: true, errorMessage: [] });
        this.lookupService.getPromotionTextStyles().then((response) => this.setState({ data: response.data?.data || response.data || [], showProgressIndicator: false })).catch(this.handleError);
    };
    getErrors = (error: any): any[] => error?.response?.data?.validationErrors || error?.response?.data?.error || [{ columnName: "Text Style", errorMessage: "Unable to save the text style." }];
    handleError = (error: any) => this.setState({ showProgressIndicator: false, loadPanelVisible: false, errorMessage: this.getErrors(error) });
    onRowInserting = (e: any) => {
        const data = { id: 0, value: e.data?.value || "" };
        this.setState({ loadPanelVisible: true, errorMessage: [] });
        this.lookupService.savePromotionTextStyle(data).then(this.loadData).catch((error) => { e.cancel = true; this.setState({ errorMessage: this.getErrors(error) }); }).finally(() => this.setState({ loadPanelVisible: false }));
    };
    onRowUpdating = (e: any) => {
        const data = { id: e.oldData.id, value: e.newData.value ?? e.oldData.value };
        this.setState({ loadPanelVisible: true, errorMessage: [] });
        this.lookupService.savePromotionTextStyle(data).then(this.loadData).catch((error) => { e.cancel = true; this.setState({ errorMessage: this.getErrors(error) }); }).finally(() => this.setState({ loadPanelVisible: false }));
    };
    onRowRemoving = (e: any) => {
        this.setState({ loadPanelVisible: true, errorMessage: [] });
        this.lookupService.deletePromotionTextStyle(String(e.data.id)).then(this.loadData).catch((error) => { e.cancel = true; this.setState({ errorMessage: this.getErrors(error) }); }).finally(() => this.setState({ loadPanelVisible: false }));
    };
    onEditCancelled = (_e: onEditCanceledEvent) => this.setState({ errorMessage: [] });
    onExporting = (e: onExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Promotion Text Styles");
        exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => workbook.xlsx.writeBuffer().then((buffer) => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "PromotionTextStyles.xlsx")));
        e.cancel = true;
    };
    renderErrors = () => this.state.errorMessage?.length ? <span className="unscheduled-shift"><ul>{this.state.errorMessage.map((item: any, index: number) => <li key={index}>{item.columnName}: {item.errorMessage || item}</li>)}</ul></span> : null;
    render() {
        return <div>
            {this.renderErrors()}
            {this.state.showProgressIndicator ? <div className="starter-template"><LoadIndicator id="promotion-text-styles-indicator" height={100} width={100} visible={true} /></div> : <>
                <LoadPanel shadingColor="rgba(0,0,0,0.4)" visible={this.state.loadPanelVisible} />
                <div className="view-clients-style-grid">
                <DataGrid dataSource={this.state.data} showBorders={false} showColumnLines={false} hoverStateEnabled={true} columnAutoWidth={true}
                    allowColumnReordering={true} allowColumnResizing={true} columnResizingMode="widget" columnMinWidth={100} keyExpr="id" onExporting={this.onExporting}
                    onRowInserting={this.onRowInserting} onRowUpdating={this.onRowUpdating} onRowRemoving={this.onRowRemoving} onEditCanceling={this.onEditCancelled}>
                    <SearchPanel visible={true} placeholder="Search" />
                    <GroupPanel visible={true} />
                    <FilterPanel visible={true} />
                    <Scrolling useNative={true} showScrollbar="always" />
                    <Export enabled={true} />
                    <Sorting mode="single" />
                    <Paging defaultPageSize={10} />
                    <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                    <Editing mode="row" useIcons={true} allowAdding={true} allowUpdating={true} allowDeleting={true} />
                    <Column type="buttons" buttons={["edit", "delete"]} />
                    <Column dataField="id" caption="TEXT STYLE ID" allowEditing={false} />
                    <Column dataField="value" caption="TEXT STYLE NAME" />
                </DataGrid>
                </div>
            </>}
        </div>;
    }
}
